import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Loader2, ScrollText, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { auditActionLabel, auditChangeLines, auditChangeSummary, auditValueText } from "../../../lib/auditAction";
import { getClientLog, type ClientLogRow } from "../../../lib/crmLogs";
import { fmtDateTime } from "../../ui";
import { auditMeta } from "../_shared/auditMeta";

type T = ReturnType<typeof useT>;

/**
 * One client's LOG - the paper trail, as against the timeline next door.
 *
 * ⚠ THIS IS NOT THE ACTIVITY TAB, AND THE DIFFERENCE IS THE REASON IT EXISTS.
 * The Timeline tab lists the OBJECTS attached to a customer - their invoices,
 * projects, tickets and mail threads - and answers "what is going on with this
 * client". This one lists ACTIONS and MESSAGES, each with a person and a
 * timestamp, and answers "who touched this client, when, and what did we
 * actually say to them". The first is a table of contents; this is the record.
 *
 * Two sources arrive already merged from the API: ledger rows (`CrmAuditLog`,
 * which is where the client-facing mail that leaves no message row lands -
 * invoices, portal invites, ticket answers) and individual e-mails, one entry
 * per letter rather than one per thread, in both directions.
 *
 * ⚠ The ledger half cannot be backfilled and starts on the day it shipped. The
 * mail half does not - it reaches as far back as the mailbox does - so a young
 * client shows letters and no actions, and that is correct rather than broken.
 * The line under the list says so.
 */

/** The lanes, and the order their chips appear in. */
type Lane = "all" | "audit" | "in" | "out";

const LANES: { key: Exclude<Lane, "all">; icon: LucideIcon; tone: string }[] = [
  // Mail first: it is what an operator opens this tab looking for, and it is
  // the half that has history on day one.
  { key: "in", icon: ArrowDownLeft, tone: "bg-sky-500/15 text-sky-500" },
  { key: "out", icon: ArrowUpRight, tone: "bg-emerald-500/15 text-emerald-600" },
  { key: "audit", icon: ScrollText, tone: "bg-slate-400/15 text-slate-500" },
];

/** Which lane one entry belongs to. The same rule the API counts by. */
function laneOf(row: ClientLogRow): Exclude<Lane, "all"> {
  if (row.source !== "mail") return "audit";
  return row.direction === "inbound" ? "in" : "out";
}

export function ClientLogs({ t, clientId }: { t: T; clientId: string }) {
  const [rows, setRows] = useState<ClientLogRow[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [failed, setFailed] = useState(false);
  const [lane, setLane] = useState<Lane>("all");
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    getClientLog(clientId, { limit: 300 })
      .then((r) => {
        if (!alive) return;
        setRows(r.rows);
        setCounts(r.counts);
        setFailed(false);
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        setCounts({});
        // A failed read and an empty log are different facts. Saying "nothing
        // has happened" when the request never landed sends somebody looking in
        // the wrong place.
        setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [clientId]);

  if (rows === null) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <ScrollText className="mx-auto mb-2 size-6 opacity-40" />
        {t(failed ? "clients.logsFailed" : "clients.logsEmpty")}
      </div>
    );
  }

  const visible = lane === "all" ? rows : rows.filter((r) => laneOf(r) === lane);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        <LaneChip active={lane === "all"} onClick={() => setLane("all")} label={t("clients.log.all")} count={rows.length} />
        {LANES.filter((l) => counts[l.key]).map((l) => (
          <LaneChip
            key={l.key}
            active={lane === l.key}
            onClick={() => setLane(l.key)}
            label={t(`clients.log.${l.key}`)}
            count={counts[l.key] ?? 0}
            icon={l.icon}
          />
        ))}
      </div>

      <ol className="relative space-y-1 before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-px before:bg-border">
        {visible.map((r) => (
          <LogEntry
            key={r.id}
            t={t}
            row={r}
            open={open === r.id}
            onToggle={() => setOpen((cur) => (cur === r.id ? null : r.id))}
          />
        ))}
      </ol>

      <p className="text-[11.5px] text-muted-foreground">{t("clients.logsSince")}</p>
    </div>
  );
}

function LogEntry({ t, row, open, onToggle }: { t: T; row: ClientLogRow; open: boolean; onToggle: () => void }) {
  const mail = row.source === "mail";
  const lane = LANES.find((l) => l.key === laneOf(row));
  const meta = auditMeta(row.action);
  // Mail wears its DIRECTION, everything else wears its module. An arrow says
  // the one thing a reader of a correspondence log needs at a glance, and an
  // envelope on every second row would say nothing at all.
  const Icon = mail ? (lane?.icon ?? meta.icon) : meta.icon;
  const dot = mail ? (lane?.tone ?? meta.dot) : meta.dot;

  const lines = auditChangeLines(row.meta);
  const hasDetail = lines.length > 0;
  // What changed, in one line, so the row is worth reading without opening it.
  // A letter keeps its own snippet; only a ledger row borrows its diff.
  const summary = row.subtitle ?? auditChangeSummary(lines);

  const body = (
    <>
      <span className={cn("relative z-10 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background", dot)}>
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {auditActionLabel(row.action, t)}
          </span>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">{fmtDateTime(row.at)}</span>
        </div>
        {row.title ? <p className="truncate font-medium">{row.title}</p> : null}
        {summary ? <p className="truncate text-xs text-muted-foreground">{summary}</p> : null}
        <p className="text-[11px] text-muted-foreground">
          {/* Actor and counterpart on one line, because on a letter they are the
              two ends of the same fact: who sent it and to whom. A row with
              neither is a system action, and the type is what says so. */}
          {[row.actorName ?? t(`clients.log.actor.${row.actorType}`), row.counterpart]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
    </>
  );

  return (
    <li>
      {row.link ? (
        <Link to={row.link} className="flex gap-3 rounded-lg p-2 transition-colors hover:bg-accent/50">
          {body}
        </Link>
      ) : hasDetail ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full gap-3 rounded-lg p-2 text-left transition-colors hover:bg-accent/50"
        >
          {body}
        </button>
      ) : (
        <div className="flex gap-3 rounded-lg p-2">{body}</div>
      )}

      {open && hasDetail ? (
        <dl className="ml-11 grid gap-1.5 rounded-xl bg-muted/40 px-3 py-2 text-[12px]">
          {lines.map((l) => (
            <div key={l.field} className="grid gap-1 sm:grid-cols-[minmax(0,130px)_1fr_1fr]">
              <dt className="text-muted-foreground">{l.field}</dt>
              <dd className="truncate line-through opacity-60">{auditValueText(l.before)}</dd>
              <dd className="truncate">{auditValueText(l.after)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}

function LaneChip({ active, onClick, label, count, icon: Icon }: {
  active: boolean; onClick: () => void; label: string; count: number; icon?: LucideIcon;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-accent",
      )}
    >
      {Icon && <Icon className="size-3" />}
      {label}
      <span className={cn("rounded-full px-1 text-[10px] tabular-nums", active ? "bg-primary-foreground/20" : "bg-muted")}>{count}</span>
    </button>
  );
}
